import { useEffect, useId, useRef, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { sq } from '@/i18n/sq';
import { cx } from '@/lib/cx';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string | undefined;
  wide?: boolean;
  footer?: ReactNode;
  children?: ReactNode;
  id?: string;
  /** Extra class on .modal-card (the prototype adds e.g. "wide"). */
  cardClassName?: string;
}

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
}

/**
 * Prototype .modal/.modal-card (CSS line 45) with closeModal() semantics: a click on the backdrop closes
 * (window click handler, line 3134), Escape closes, focus is trapped inside the card and returned to the
 * opener when the modal closes.
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  wide = false,
  footer,
  children,
  id,
  cardClassName,
}: ModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  // Close only when the press STARTED on the backdrop. A click whose mousedown was inside the card —
  // selecting the text of a price, dragging slightly past the edge — used to shut the modal and throw
  // the entry away, which is the other half of why it felt easy to close by accident.
  const pressedBackdrop = useRef(false);
  const titleId = useId();
  const subtitleId = useId();

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const card = cardRef.current;
    if (card) {
      const body = card.querySelector<HTMLElement>('.modal-body');
      const first = (body ? focusables(body)[0] : undefined) ?? focusables(card)[0] ?? card;
      first.focus();
    }
    const onDocumentKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onDocumentKey);
    return () => {
      document.removeEventListener('keydown', onDocumentKey);
      opener?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const onBackdropPointerDown = (event: MouseEvent<HTMLDivElement>) => {
    pressedBackdrop.current = event.target === event.currentTarget;
  };

  const onBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && pressedBackdrop.current) onClose();
    pressedBackdrop.current = false;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab' || !cardRef.current) return;
    const items = focusables(cardRef.current);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) {
      event.preventDefault();
      return;
    }
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === cardRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="modal"
      id={id}
      onMouseDown={onBackdropPointerDown}
      onClick={onBackdropClick}
      onKeyDown={onKeyDown}
      data-testid="modal-backdrop"
    >
      <div
        ref={cardRef}
        className={cx('modal-card', wide && 'wide', cardClassName)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitle ? subtitleId : undefined}
        tabIndex={-1}
      >
        <div className="modal-head">
          <div>
            <h3 id={titleId}>{title}</h3>
            {subtitle ? <p id={subtitleId}>{subtitle}</p> : null}
          </div>
          <button type="button" className="close-btn" aria-label={sq.common.close} onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
